import { client } from '@repo/api-kit/queries/utils';
import {
  registerPartnersBaseURL,
  registerPartnersNamespace,
} from '@repo/shared-kit/constants/hosts';

import type { PaymentIntent, PaymentIntentAdapter } from './types';

/**
 * Backend payment-intent shape, as returned by the Register Partners API
 * (`POST .../payment_intents` and `POST .../payment_intents/:id/confirm`).
 * Unlike the Register API, this backend speaks JSON:API.
 */
interface BackendPaymentIntentResponse {
  data: {
    id: string;
    type: 'payment_intents';
    attributes: {
      client_secret: string;
      requires_action: boolean;
    };
  };
}

interface CreateRegisterPartnersPaymentIntentAdapterParams {
  registrationId: string;
}

/**
 * Bridges the kit's `PaymentIntentAdapter` contract to the Register Partners
 * API, which is JSON:API: request bodies use `data.attributes` (snake_case)
 * plus a `registration` relationship linkage, and responses wrap attributes
 * under `data.attributes`.
 */
export function createRegisterPartnersPaymentIntentAdapter({
  registrationId,
}: CreateRegisterPartnersPaymentIntentAdapterParams): PaymentIntentAdapter {
  const baseUrl = `${registerPartnersBaseURL}/${registerPartnersNamespace}`;

  return {
    async createPaymentIntent({ paymentMethodId, pricePlanId }) {
      const response = await client<BackendPaymentIntentResponse>(`${baseUrl}/payment_intents`, {
        method: 'POST',
        body: JSON.stringify({
          data: {
            type: 'payment_intents',
            attributes: {
              price_plan_id: pricePlanId,
              payment_method_id: paymentMethodId,
            },
            relationships: {
              registration: {
                data: { type: 'registrations', id: registrationId },
              },
            },
          },
        }),
      });

      return toPaymentIntent(response);
    },

    async confirmPaymentIntent(intentId) {
      const response = await client<BackendPaymentIntentResponse>(
        `${baseUrl}/payment_intents/${intentId}/confirm`,
        { method: 'POST' }
      );

      return toPaymentIntent(response);
    },
  };
}

function toPaymentIntent({ data }: BackendPaymentIntentResponse): PaymentIntent {
  return {
    id: data.id,
    clientSecret: data.attributes.client_secret,
    requiresAction: data.attributes.requires_action,
  };
}
